import React, { useState } from "react";
import { InputNumber } from "antd";

/**
 * Coerce DollarInputNumber / stringMode values to a finite number for sums.
 * stringMode keeps onChange as string while typing; never use bare `+` on those values.
 */
export const toMoneyNumber = (value: unknown): number => {
    if (value == null || value === "") return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const n = Number(String(value).replace(/[$,\s]/g, ""));
    return Number.isFinite(n) ? n : 0;
};

/** Legacy / shared float sum (unchanged behavior for old lender loan form). */
export const sumMoney = (values: unknown[]): number =>
    values.reduce<number>((sum, value) => sum + toMoneyNumber(value), 0);

/** Round to cents to avoid IEEE float artifacts (e.g. 200.33+100.3 → 500.96000000000004). */
export const roundMoney = (value: number, decimals = 2): number => {
    if (!Number.isFinite(value)) return 0;
    const factor = 10 ** decimals;
    return Math.round((value + Number.EPSILON) * factor) / factor;
};

/** V2-only: sum in integer cents then convert back. */
export const sumMoneyV2 = (values: unknown[]): number => {
    const cents = values.reduce<number>((sum, value) => {
        return sum + Math.round(toMoneyNumber(value) * 100);
    }, 0);
    return cents / 100;
};

/** Incomplete stringMode drafts (e.g. "12.") must stay strings so decimals can be typed. */
const isIncompleteMoneyInput = (value: unknown): boolean =>
    typeof value === "string" && (value === "-" || /^-?\d*\.$/.test(value));

/**
 * Normalize InputNumber onChange payloads to finite numbers.
 * Keeps trailing "." / lone "-" as strings while typing under stringMode.
 */
export const normalizeMoneyOnChange = (
    value: unknown,
    onChange?: (next: number | string | null) => void
) => {
    if (!onChange) return;
    if (value === null || value === undefined || value === "") {
        onChange(null);
        return;
    }
    if (isIncompleteMoneyInput(value)) {
        onChange(value as string);
        return;
    }
    onChange(toMoneyNumber(value));
};

/** Truncate to at most maxDecimalPlaces while typing (InputNumber parser). */
export const parseMoneyTyping = (
    value: string | undefined,
    maxDecimalPlaces = 2,
    allowNegative = false
) => {
    if (!value && value !== "0") return "";
    const raw = value.toString().replace(/[$,\s]/g, "");
    if (allowNegative && raw.trim() === "-") return "-";
    const negative = allowNegative && raw.trim().startsWith("-");
    let cleaned = raw.replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
        cleaned =
            cleaned.slice(0, firstDot + 1) +
            cleaned.slice(firstDot + 1).replace(/\./g, "");
    }
    if (firstDot === -1) {
        if (!cleaned) return negative ? "-" : "";
        return `${negative ? "-" : ""}${cleaned}`;
    }
    const [intPart = "", ...rest] = cleaned.split(".");
    const decimalRaw = rest.join("") || "";
    const decimal =
        maxDecimalPlaces == null
            ? decimalRaw
            : decimalRaw.slice(0, maxDecimalPlaces);
    if (decimal === "" && raw.replace(/^-/, "").endsWith(".")) {
        return `${negative ? "-" : ""}${intPart || "0"}.`;
    }
    if (decimal === "") {
        if (!intPart) return negative ? "-" : "";
        return `${negative ? "-" : ""}${intPart}`;
    }
    return `${negative ? "-" : ""}${intPart || "0"}.${decimal}`;
};

// Shared / legacy DollarInputNumber — keep behavior for old lender loan form.
export const DollarInputNumber = (props) => {
    const allowNegative = props.min !== undefined && Number(props.min) < 0;
    const {
        formatter: _formatter,
        parser: _parser,
        precision: _precision,
        stringMode: _stringMode,
        onChange,
        onBlur,
        onPressEnter,
        ...rest
    } = props;
    return (
        <InputNumber
            prefix="$"
            min={0}
            max={100000000.0}
            className="w-100"
            size="large"
            {...rest}
            stringMode
            formatter={(value) => {
                if (!value && value !== 0) return "";
                const strValue = value.toString();
                if (strValue.includes(".")) {
                    const parts = strValue.split(".");
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    return parts.join(".");
                }
                return strValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }}
            parser={(value) => parseMoneyTyping(value, 2, allowNegative)}
            onChange={(value) => normalizeMoneyOnChange(value, onChange)}
            onBlur={onBlur}
            onPressEnter={onPressEnter}
        />
    );
};

/**
 * V2-only: local draft while focused so trailing "." is not swallowed by
 * controlled parent number values (same idea as MoneyPrefixField).
 */
export const DollarInputNumberV2 = (props) => {
    const allowNegative = props.min !== undefined && Number(props.min) < 0;
    const {
        formatter: _formatter,
        parser: _parser,
        precision: _precision,
        stringMode: _stringMode,
        onChange,
        onBlur,
        onFocus,
        onPressEnter,
        value,
        ...rest
    } = props;
    const [draft, setDraft] = useState<string | null>(null);
    const shown = draft !== null ? draft : value;

    return (
        <InputNumber
            prefix="$"
            min={0}
            max={100000000.0}
            className="w-100"
            size="large"
            {...rest}
            value={shown}
            stringMode
            formatter={(displayValue) => {
                if (!displayValue && displayValue !== 0) return "";
                const strValue = displayValue.toString();
                if (strValue.includes(".")) {
                    const parts = strValue.split(".");
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    return parts.join(".");
                }
                return strValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }}
            parser={(displayValue) =>
                parseMoneyTyping(
                    displayValue == null
                        ? displayValue
                        : String(displayValue).replace(/[。．]/g, "."),
                    2,
                    allowNegative,
                )
            }
            onFocus={(e) => {
                setDraft(
                    value == null || value === ""
                        ? ""
                        : String(value).replace(/,/g, ""),
                );
                onFocus?.(e);
            }}
            onChange={(next) => {
                const normalized =
                    next == null || next === ""
                        ? ""
                        : String(next).replace(/[。．]/g, ".");
                const parsed =
                    normalized === ""
                        ? ""
                        : parseMoneyTyping(normalized, 2, allowNegative);
                setDraft(parsed);
                normalizeMoneyOnChange(parsed === "" ? null : parsed, onChange);
            }}
            onBlur={(e) => {
                if (draft !== null && isIncompleteMoneyInput(draft)) {
                    normalizeMoneyOnChange(toMoneyNumber(draft), onChange);
                }
                setDraft(null);
                onBlur?.(e);
            }}
            onPressEnter={onPressEnter}
        />
    );
};
